import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_EMOJIS = [
  { emoji: '😊', labelKey: 'dailyMood.moodHappy' },
  { emoji: '🥰', labelKey: 'dailyMood.moodLoved' },
  { emoji: '😌', labelKey: 'dailyMood.moodPeaceful' },
  { emoji: '🤗', labelKey: 'dailyMood.moodGrateful' },
  { emoji: '🥳', labelKey: 'dailyMood.moodExcited' },
  { emoji: '😤', labelKey: 'dailyMood.moodProud' },
  { emoji: '🤔', labelKey: 'dailyMood.moodThoughtful' },
  { emoji: '😐', labelKey: 'dailyMood.moodNeutral' },
  { emoji: '😕', labelKey: 'dailyMood.moodConfused' },
  { emoji: '😔', labelKey: 'dailyMood.moodDisappointed' },
  { emoji: '😢', labelKey: 'dailyMood.moodSad' },
  { emoji: '😫', labelKey: 'dailyMood.moodExhausted' },
  { emoji: '😠', labelKey: 'dailyMood.moodAngry' },
  { emoji: '😨', labelKey: 'dailyMood.moodAnxious' },
  { emoji: '😴', labelKey: 'dailyMood.moodTired' },
  { emoji: '🤒', labelKey: 'dailyMood.moodSick' },
];

interface EmojiPickerProps {
  selected: string;
  onSelect: (emoji: string) => void;
  onNoteChange?: (note: string) => void;
  note?: string;
}

export default function EmojiPicker({
  selected,
  onSelect,
  onNoteChange,
  note
}: EmojiPickerProps) {
  const { t } = useTranslation();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [customEmoji, setCustomEmoji] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [localNote, setLocalNote] = useState(note);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (selected && !DEFAULT_EMOJIS.find(e => e.emoji === selected)) {
      setCustomEmoji(selected);
    } else if (selected && customEmoji) {
      setCustomEmoji(null);
    }
  }, [selected]);

  useEffect(() => {
    setLocalNote(note);
  }, [note]);

  useEffect(() => {
    if (isTyping) {
      const blurTimer = setTimeout(() => {
        setIsTyping(false);
        textareaRef.current?.blur();
      }, 2000);

      const updateTimer = setTimeout(() => {
        onNoteChange?.(localNote || '');
      }, 500);

      return () => {
        clearTimeout(blurTimer);
        clearTimeout(updateTimer);
      };
    }
  }, [isTyping, localNote, onNoteChange]);

  const handleNoteChange = (value: string) => {
    setIsTyping(true);
    setLocalNote(value);
  };

  const displayedEmojis = customEmoji
    ? [{ emoji: customEmoji, labelKey: 'dailyMood.moodCustom' as const }, ...DEFAULT_EMOJIS.slice(1)]
    : DEFAULT_EMOJIS;

  const handleEmojiSelect = (emoji: string) => {
    if (!DEFAULT_EMOJIS.find(e => e.emoji === emoji)) {
      setCustomEmoji(emoji);
    } else {
      setCustomEmoji(null);
    }
    onSelect(emoji);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-8 gap-2">
        {displayedEmojis.map(({ emoji, labelKey }) => (
          <button
            key={emoji}
            className={`aspect-square flex items-center justify-center p-2 rounded-xl 
              transition-all transform hover:scale-105 hover:shadow-md text-2xl
              ${selected === emoji
                ? 'bg-yellow-50 ring-2 ring-yellow-400 shadow-lg scale-105'
                : 'bg-white shadow hover:bg-gray-50'
              }`}
            title={t(labelKey)}
            onClick={() => handleEmojiSelect(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>

      {selected && (
        <div className="animate-fadeIn">
          <textarea
            ref={textareaRef}
            className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none
              focus:ring-2 focus:ring-yellow-400 text-sm bg-white transition-all"
            placeholder={t('dailyMood.feelingPlaceholder')}
            rows={2}
            value={localNote}
            onChange={(e) => handleNoteChange(e.target.value)}
          />
        </div>
      )}

      <button
        className="w-full py-2 px-4 text-sm text-gray-600 hover:text-gray-900
          flex items-center justify-center gap-2 rounded-xl hover:bg-gray-100 transition-colors"
        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
      >
        <span>{t('dailyMood.chooseAnother')}</span>
        <span className="text-xs">✨</span>
      </button>

      {showEmojiPicker && (
        <div className="absolute z-10 bottom-0 left-0 right-0 bg-white shadow-lg rounded-t-xl">
          <div className="p-2 border-b flex justify-between items-center">
            <h3 className="font-medium">{t('dailyMood.pickEmoji')}</h3>
            <button
              className="p-2 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowEmojiPicker(false)}
            >
              ✕
            </button>
          </div>
          <Picker
            data={data}
            onEmojiSelect={(emoji: { native: string }) => {
              handleEmojiSelect(emoji.native);
              setShowEmojiPicker(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
